import { useRef, useEffect, useState } from 'react';

export interface AIAnswerItem {
  question: string;
  answer: string;
}

interface Props {
  title?: string;
  items: AIAnswerItem[];
}

export default function AIAnswerBlock({ title = 'Flooring Services — Explained', items }: Props) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  const [open, setOpen] = useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-24 lg:py-32"
      style={{ background: '#FFFFFF' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-28px)',
              transition: 'opacity 0.75s ease, transform 0.75s ease',
            }}
          >
            <span className="section-tag mb-4 block">AI Answer Block</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-charcoal leading-tight mb-6">
              {title}
            </h2>
            <div className="gold-line mb-8" />
            <p className="text-warm-gray text-base leading-relaxed">
              Clear, structured answers about Value Carpet Inc — optimized for Google, AI assistants, and voice search.
            </p>
            <div className="mt-10 p-6 bg-cream rounded-lg border border-cream-dark">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-charcoal">
                  <i className="ri-robot-line text-gold text-sm"></i>
                </span>
                <p className="text-charcoal font-semibold text-sm">Optimized for AI Search</p>
              </div>
              <p className="text-warm-gray text-sm leading-relaxed">
                These answers are structured for ChatGPT, Gemini, Siri, and Google AI to surface Value Carpet Inc for relevant queries.
              </p>
            </div>
          </div>

          {/* Right — Accordion */}
          <div
            className="space-y-3"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(28px)',
              transition: 'opacity 0.75s ease 0.15s, transform 0.75s ease 0.15s',
            }}
          >
            {items.map((item, i) => (
              <div
                key={i}
                className="border border-cream-dark rounded-lg overflow-hidden bg-cream"
              >
                <button
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-cream-dark/60 transition-colors"
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <span className="font-medium text-charcoal text-sm leading-snug pr-2">
                    {item.question}
                  </span>
                  <span
                    className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-gold transition-transform duration-200"
                    style={{ transform: open === i ? 'rotate(45deg)' : 'none' }}
                  >
                    <i className="ri-add-line text-base"></i>
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: open === i ? '300px' : '0' }}
                >
                  <p className="px-6 pb-5 text-warm-gray text-sm leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
